import { useContext, useLayoutEffect, useState, useMemo, HTMLAttributes, useCallback } from 'react';
import AccordionContext from '../Accordion/AccordionContext';
import AccordionPanelContext from './AccordionPanelContext';
import useStyles from './AccordionPanel.style';

export interface IAccordionPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** If the panel is nested the nestedSection class will be applied. */
  isNested?: boolean;
  /** Id of the accordion panel */
  panelId: string;
  /** Will display the panel in the open state if true */
  isOpen?: boolean;
  /** data-qa attribute applied to panel section */
  dataQa?: string;
}

let uniqueId = 0;
const AccordionPanel = ({
  children,
  className,
  dataQa,
  isNested = false,
  isOpen = false,
  panelId,
}: IAccordionPanelProps) => {
  const { handleSetCurrentOpenPanelsIdsArr, openPanelIdsArr } = useContext(AccordionContext);
  const classes = useStyles();

  // In case of AccordionPanel exists out of Accordion scope local state will be responsible
  // for managing Accordion Panel Toggle functionality.
  const panelIsPartOfAccordion = useMemo(() => openPanelIdsArr !== undefined, [openPanelIdsArr]);
  const [isPanelOpenLocally, setIsPanelOpenLocally] = useState(isOpen);

  const managePanelOpenStateLocally = useCallback(() => {
    setIsPanelOpenLocally((prevOpenState) => !prevOpenState);
  }, []);

  const dataQAFallback = useMemo(() => `accordion-${uniqueId++}`, []); // eslint-disable-line no-plusplus

  // Hook useLayoutEffect is called once before AccordionPanel has been rendered to update
  // accordion's openPanelIdsArr in case of `isOpen` prop has been passed to AccordionPanel.
  useLayoutEffect(() => {
    if (isOpen && panelIsPartOfAccordion) {
      handleSetCurrentOpenPanelsIdsArr!(panelId);
    }
  }, [handleSetCurrentOpenPanelsIdsArr, isOpen, panelId, panelIsPartOfAccordion]);

  const contextValue = useMemo(
    () => ({
      isOpen: panelIsPartOfAccordion
        ? openPanelIdsArr?.indexOf(panelId) !== -1
        : isPanelOpenLocally,
      handleSetCurrentOpenPanelsIdsArr: panelIsPartOfAccordion
        ? handleSetCurrentOpenPanelsIdsArr!
        : managePanelOpenStateLocally,
      panelId,
    }),
    [
      panelIsPartOfAccordion,
      openPanelIdsArr,
      isPanelOpenLocally,
      handleSetCurrentOpenPanelsIdsArr,
      managePanelOpenStateLocally,
      panelId,
    ]
  );

  return (
    <AccordionPanelContext.Provider value={contextValue}>
      <section
        className={`${
          isNested === true ? classes.nestedSection : classes.parentSection
        } ${className}`}
        data-qa={dataQa || dataQAFallback}
      >
        {children}
      </section>
    </AccordionPanelContext.Provider>
  );
};

AccordionPanel.displayName = 'AccordionPanel';

export default AccordionPanel;
